import type {
  Job,
  CustomerVideo,
  CustomerPhoto,
} from '../../../stores/useAppointmentsStore';
import { adaptPhotos, type AppointmentWirePhoto } from './appointment';

/**
 * Authoritative wire shape returned by `/v2/jobs/:id`.
 *
 * Source of truth:
 *   - /Users/chris/Sera/api/app/views/v2/jobs/show.json.jbuilder
 *   - /Users/chris/Sera/api/app/views/v2/jobs/_job.json.jbuilder
 *   - /Users/chris/Sera/api/app/helpers/jobs_helper.rb#job_api_links
 *
 * What the Vue 3 customer portal actually reads from a Job (post-fix):
 *   - `id`
 *   - `links.reserve_s3_photos.href` — uploadPhotos
 *   - `invoice_id` — temporary bridge for invoiceTotalLabel; sourced from
 *     `invoice.invoice_id` when present.
 *
 * Settings (late_fee_per_minute_cents / max_late_fee_cents) was moved to the
 * `Appointment` adapter — it lives on the appointment wire, not the job, and
 * the Vue 2 legacy app consumed it from the appointment too.
 */
export interface JobWireLinks {
  reserve_s3_photos?: { href: string; method?: string } | null;
}

/**
 * Money fields are nested under a `financials` block in the v2 invoice
 * partial (../api/app/views/v2/invoices/_invoice.json.jbuilder:66-76), NOT
 * at the invoice root. Only `invoice_id` (= invoice.id) sits at the root.
 */
export interface JobWireInvoiceFinancials {
  subtotal_cents?: number;
  total_cents?: number;
  total_transactions_cents?: number;
  balance_cents?: number;
}

export interface JobWireInvoice {
  invoice_id?: number;
  /**
   * Some payloads carry a top-level `id` on the embedded invoice block
   * (matches `Job.invoice.id` consumed by `InvoiceCard.vue`). Optional
   * because the legacy v2 emit only had `invoice_id`.
   */
  id?: number;
  // The current v2 invoice wire (_invoice.json.jbuilder) emits neither
  // `invoice_number` nor `paid_at` — both stay typed because InvoiceCard binds
  // them and `undefined` is safe, but readers should not assume they arrive.
  invoice_number?: string | null;
  paid_at?: string | null;
  // Due date for an unpaid invoice (WR-07). Not always emitted by the current
  // v2 wire; passed through to Job.invoice.due_at when present so the
  // appointment-detail InvoiceCard can type-safely read it. Phase 5 wires the
  // canonical due date.
  due_at?: string | null;
  financials?: JobWireInvoiceFinancials | null;
}

/**
 * CarrierWave-mounted file fields (`video.file`, `video.thumbnail`) marshal to
 * `{ url: '...' }` in jbuilder; some legacy/mocked payloads model them as a
 * bare URL string. Accept both shapes.
 */
export type JobWireFileRef =
  | string
  | { url?: string | null }
  | null
  | undefined;

/**
 * Video as emitted by `/v2/jobs/:id` via `v2/videos/_video.json.jbuilder`.
 * Videos live on the JOB wire, not the v1 appointment wire (which only
 * serializes photos + documents) — so the appointment-detail surface sources
 * its videos from here. `duration` lives under `metadata` (jsonb, seconds).
 */
export interface JobWireVideo {
  id: number;
  file?: JobWireFileRef;
  thumbnail?: JobWireFileRef;
  download_url?: string | null;
  customer_visible?: boolean;
  created_at?: string;
  created_by_name?: string;
  // Free-text media caption (PODZ-1830). Assumed wire field name `caption`;
  // single change point if PODZ-1823 ships a different name. Absent until
  // that backend deploys.
  caption?: string;
  // Upload source enum (PODZ-1830) — video wire carries `source` per
  // _video.json.jbuilder:19. Threaded verbatim into CustomerVideo.source;
  // enum→label mapping is centralized in sourceLabel() (@sera/core).
  source?: string;
  metadata?: { duration?: number | null } | null;
}

export interface JobWire {
  id: number;
  links?: JobWireLinks | null;
  invoice?: JobWireInvoice | null;
  quotes?: unknown[];
  videos?: JobWireVideo[] | null;
  /**
   * Photos as emitted by `/v2/jobs/:id` via `v2/photos/_photo.json.jbuilder`.
   * Unlike the lean v1 appointment serializer, the v2 job photo carries
   * `created_by_name`, `name`, `source`, `created_at`, and `customer_visible`
   * — so the appointment-detail surface sources its photos from HERE to render
   * the upload-meta footer's "Uploaded by {name}" segment (07.1-05 gap-close).
   * Typed as `AppointmentWirePhoto[]` because the v2 shape is a superset and
   * the adapter only reads the shared fields.
   */
  photos?: AppointmentWirePhoto[] | null;
}

function fileRefUrl(ref: JobWireFileRef): string {
  if (!ref) return '';
  if (typeof ref === 'string') return ref;
  return ref.url ?? '';
}

/**
 * Copy the optional video meta fields (createdAt, createdByName, caption,
 * source) onto `out` as conditional-assigns so an absent wire value leaves the
 * key off entirely and the footer omits that segment. Extracted from
 * `adaptJobVideo` to keep both functions under the 10-complexity ceiling
 * enforced by the project's eslint config.
 *
 * caption + source are the PODZ-1830 media-footer fields; source's human label
 * is resolved downstream via sourceLabel() (@sera/core).
 */
function assignVideoMeta(out: CustomerVideo, v: JobWireVideo): void {
  if (v.created_at) out.createdAt = v.created_at;
  if (v.created_by_name) out.createdByName = v.created_by_name;
  if (v.caption) out.caption = v.caption;
  if (v.source) out.source = v.source;
}

/**
 * Map a single v2 video wire entry to `CustomerVideo`. The embed URL comes
 * from `file` (the raw uploader URL plays inline); `download_url` is the
 * attachment-disposition fallback. Duration is read from `metadata.duration`
 * and rounded to whole seconds; optional meta fields are threaded via
 * `assignVideoMeta`.
 */
function adaptJobVideo(v: JobWireVideo): CustomerVideo {
  const out: CustomerVideo = {
    id: v.id,
    url: fileRefUrl(v.file) || (v.download_url ?? ''),
    thumbUrl: fileRefUrl(v.thumbnail),
    customer_visible: true,
  };
  const dur = v.metadata?.duration;
  if (typeof dur === 'number' && Number.isFinite(dur) && dur > 0) {
    out.duration_seconds = Math.round(dur);
  }
  assignVideoMeta(out, v);
  return out;
}

/**
 * Map the v2 video wire array to `CustomerVideo[]`. Filters to
 * customer-visible videos (techs may attach internal-only clips) before
 * mapping each entry via `adaptJobVideo`.
 */
function adaptJobVideos(
  wire: JobWireVideo[] | null | undefined,
): CustomerVideo[] {
  if (!Array.isArray(wire)) return [];
  return wire.filter((v) => v.customer_visible === true).map(adaptJobVideo);
}

/**
 * Map the v2 job-wire photo array to `CustomerPhoto[]`. Filters to
 * customer-visible photos (techs/admins may attach internal-only shots — the
 * sample payload includes an admin photo with `customer_visible: false` that
 * MUST be excluded), mirroring `adaptJobVideos`, then delegates the field
 * mapping to the shared `adaptPhotos` helper (the v2 photo shape is a superset
 * of the appointment photo shape, so the mapping is not duplicated). Kept as a
 * thin wrapper so `adaptJob` stays under the eslint complexity-10 ceiling.
 */
function adaptJobPhotos(
  wire: AppointmentWirePhoto[] | null | undefined,
): CustomerPhoto[] {
  if (!Array.isArray(wire)) return [];
  return adaptPhotos(wire.filter((p) => p.customer_visible === true));
}

/**
 * Plan 04.6-10 (Rule 1 bugfix): adapt the wire's structured `invoice` block
 * into the nested `Job.invoice` shape consumed by `AppointmentDetailView.vue`
 * + `InvoiceCard.vue`. Before this fix the adapter only emitted the flat
 * bridge keys `invoice_total_cents` / `invoice_balance_cents`, so
 * `job.invoice` was undefined at runtime even when the wire emitted the
 * invoice — the completed-unpaid pill never fired (mapStatusToPillKey reads
 * `job.invoice.balance_cents`) and the cancelled-with-balance invoice card
 * never rendered against live wire data.
 *
 * Extracted to keep `adaptJob` under the 10-complexity ceiling enforced by
 * the project's eslint config.
 */
function adaptStructuredInvoice(
  w: JobWireInvoice,
): NonNullable<Job['invoice']> {
  const fin = w.financials;
  const inv: NonNullable<Job['invoice']> = {
    id: typeof w.id === 'number' ? w.id : (w.invoice_id ?? 0),
    total_cents: typeof fin?.total_cents === 'number' ? fin.total_cents : 0,
    balance_cents:
      typeof fin?.balance_cents === 'number' ? fin.balance_cents : 0,
  };
  if (w.invoice_number !== undefined) inv.invoice_number = w.invoice_number;
  if (w.paid_at !== undefined) inv.paid_at = w.paid_at;
  if (w.due_at !== undefined) inv.due_at = w.due_at;
  return inv;
}

/**
 * Surface the wire's invoice into the two flat bridge keys
 * (`invoice_total_cents`, `invoice_balance_cents`) AND the structured
 * `Job.invoice` block in one place. The flat keys are a legacy bridge the
 * Phase 4 summary view consumed via `unknown as ...` casts; the structured
 * block is the Phase 4.6 sketch-022 contract. Both live in parallel until
 * Phase 5 retires the flat bridge.
 */
function attachInvoice(adapted: Job, w: JobWireInvoice): void {
  if (w.invoice_id !== undefined) adapted.invoice_id = w.invoice_id;
  const fin = w.financials;
  if (typeof fin?.total_cents === 'number') {
    (adapted as { invoice_total_cents?: number }).invoice_total_cents =
      fin.total_cents;
  }
  if (typeof fin?.balance_cents === 'number') {
    (adapted as { invoice_balance_cents?: number }).invoice_balance_cents =
      fin.balance_cents;
  }
  adapted.invoice = adaptStructuredInvoice(w);
}

/**
 * Map the v2 job wire payload to the nested `Job` shape consumed by the UI.
 *
 * On a malformed response (missing `links` block), the adapter surfaces an
 * empty href so the upload helper degrades to an upstream error instead of
 * throwing on `.href` of undefined.
 */
export function adaptJob(wire: JobWire): Job {
  const reserveS3Photos = wire.links?.reserve_s3_photos;
  const adapted: Job = {
    id: wire.id,
    links: {
      reserve_s3_photos: reserveS3Photos
        ? { href: reserveS3Photos.href }
        : { href: '' },
    },
  };

  if (wire.invoice) {
    attachInvoice(adapted, wire.invoice);
  }

  // Plan 04.6-10: pass `quotes` array through verbatim. The quote shape lines
  // up with `Quote` (`QuoteCard` consumes `Job.quotes` directly per CONTEXT
  // D-27); the adapter doesn't reshape it because the Vue 3 interface mirrors
  // the wire field-for-field. Defensive `Array.isArray` guards against the
  // common pre-Phase-5 case of the wire emitting `null` or omitting the field.
  if (Array.isArray(wire.quotes)) {
    adapted.quotes = wire.quotes as Job['quotes'];
  }

  // Videos are a job-wire concern (the v1 appointment wire omits them). The
  // appointment-detail media card reads them from `Job.videos`.
  const videos = adaptJobVideos(wire.videos);
  if (videos.length > 0) adapted.videos = videos;

  // Photos are sourced from the RICH v2 job wire (not the lean v1 appointment
  // serializer) so the upload-meta footer can render "Uploaded by {name}" —
  // the v1 photo lacks created_by_name (07.1-05 gap-close).
  const photos = adaptJobPhotos(wire.photos);
  if (photos.length > 0) adapted.photos = photos;

  return adapted;
}
